/* @flow */
// Class announcements panel: the gym's timetable, read from its website (or typed
// in), approved once; each class is announced a few minutes before it starts.
import React, { Component } from 'react'
import zones from '../../stores/Zones'
import { observer, inject } from 'mobx-react'
import Switch from 'rc-switch'
import 'rc-switch/assets/index.css'
import '../AdsPanel/AdsPanel.css'
import { VOICES, PlayIcon, PauseIcon, BackIcon, CalendarIcon } from '../AdsPanel/shared'
import { isDevice } from '../../helpers/device'

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const DAY_NAMES = { Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday' }
const today = () => DAYS[(new Date().getDay() + 6) % 7]
const LEADS = [5, 10, 15, 20, 25, 30]
const announcementText = (c, lead) => `${(c.name || 'Spin').trim()} starts in ${lead} minutes${(c.room || '').trim() ? ` in ${c.room.trim()}` : ''}. See you there!`
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 'es'}`
const fmtClock = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
let rowSeq = 0
const newRowId = () => `new-${Date.now().toString(36)}-${rowSeq++}`


class ClassesPanel extends Component {
  state = { playingKey: '', classVoice: '', openDays: [today()] }
  audio = null

  componentDidMount () {
    this.props.studioStore.loadClasses()
  }

  componentWillUnmount () {
    this.stopPreview()
  }

  preview (key, url) {
    if (!url) return
    if (this.state.playingKey === key) {
      this.stopPreview()
      return
    }
    // Music zones: a device playing a zone would send the preview out of that
    // area's speakers, so ask first (once per visit)
    if (zones.speakerCheck(() => this.preview(key, url))) return
    this.stopPreview()
    this.props.onPreviewStart && this.props.onPreviewStart()
    this.audio = new Audio(url)
    this.audio.onended = () => this.setState({ playingKey: '' })
    this.audio.play().catch(() => this.setState({ playingKey: '' }))
    this.setState({ playingKey: key })
  }

  stopPreview () {
    if (this.audio) {
      this.audio.pause()
      this.audio = null
    }
    if (this.state.playingKey) this.setState({ playingKey: '' })
  }

  requirePro (fn) {
    const { userStore, onSubscribe } = this.props
    if (!userStore.isPremium) {
      onSubscribe && onSubscribe()
      return
    }
    fn()
  }

  setLead (delta) {
    const { studioStore } = this.props
    const c = studioStore.classes || {}
    const i = LEADS.indexOf(Number(c.lead) || 15)
    const next = LEADS[Math.min(LEADS.length - 1, Math.max(0, (i < 0 ? 2 : i) + delta))]
    if (next !== c.lead) this.requirePro(() => studioStore.saveClasses({ lead: next }))
  }

  renderSummary () {
    const { studioStore, playlistStore } = this.props
    const c = studioStore.classes
    if (!c) return null
    const setUp = c.approved && (c.classes || []).length > 0
    let status = ''
    if (c.status === 'recording') {
      status = c.progress ? `Recording announcements… ${c.progress.done} of ${c.progress.total}` : 'Recording announcements…'
    } else if (c.status === 'failed') {
      status = c.error || "The announcements couldn't be recorded. Open your timetable and approve it again."
    } else if (setUp) {
      const next = playlistStore.classAnnouncer.upcoming()
      status = !c.enabled
        ? `${plural(c.classes.length, 'class')} · switched off`
        : next
          ? `Next: ${next.cls.name} at ${next.cls.time}, announced from ${fmtClock(next.at)}`
          : `${plural(c.classes.length, 'class')} · no more today`
    }
    return (
      <div className="studio__screen">
        <header className="studio__header">
          <span className="studio__icon"><CalendarIcon /></span>
          <h2 className="studio__headline">Class announcements</h2>
        </header>
        {
          !setUp && c.status !== 'recording' && (
            <p className="studio__hint">
              We read your timetable and announce each class a few minutes before it starts, for example "Spin starts in 15 minutes in Studio 1. See you there!"
            </p>
          )
        }
        { studioStore.classesError && <div className="studio__error" role="alert">{ studioStore.classesError }</div> }
        {
          c.status === 'recording' && (
            <div className="studio__notice" role="status">
              <strong>Recording your announcements…</strong>
              <span className="studio__hint">
                { c.progress ? `${c.progress.done} of ${c.progress.total} done. ` : '' }
                One announcement per class and room, so { plural((c.classes || []).length, 'class') } { c.progress ? `need ${c.progress.total}` : 'are being recorded' }. This takes a minute or two; you can keep using the player.
              </span>
              <div className="studio__progress" aria-hidden="true">
                <span style={ { width: `${c.progress && c.progress.total ? Math.round(100 * c.progress.done / c.progress.total) : 5}%` } } />
              </div>
            </div>
          )
        }
        {
          studioStore.classesJustReady && c.status === 'ready' && (
            <div className="studio__notice" role="status">
              <strong>Your class announcements are ready.</strong>
              <span className="studio__hint"> Each class is announced { c.lead } minutes before it starts. Listen to any of them under "Edit your timetable".</span>
            </div>
          )
        }
        <div className="studio__item">
          <div className="studio__item-text">
            <span className="studio__item-title">{ setUp || c.status === 'recording' ? status : 'Not set up yet' }</span>
            { setUp && <span className="studio__item-meta">{ c.voice } · from your timetable</span> }
          </div>
          {
            setUp && (
              <Switch
                checked={ !!c.enabled }
                onChange={ (value) => this.requirePro(() => studioStore.saveClasses({ enabled: value })) }
                aria-label="Play class announcements"
              />
            )
          }
        </div>
        {
          setUp && (
            <div className="studio__lead">
              <span>Play</span>
              <button type="button" className="studio__round" aria-label="Earlier" disabled={ studioStore.savingClasses || c.lead <= LEADS[0] } onClick={ () => this.setLead(-1) }>−</button>
              <strong>{ c.lead }</strong>
              <button type="button" className="studio__round" aria-label="Later" disabled={ studioStore.savingClasses || c.lead >= LEADS[LEADS.length - 1] } onClick={ () => this.setLead(1) }>+</button>
              <span>minutes before class</span>
            </div>
          )
        }
        {
          setUp && (
            <div className="studio__group">
              <label className="studio__label" htmlFor="studio-classes-voice">Voice for every announcement</label>
              <select id="studio-classes-voice" className="studio__input" value={ c.voice || 'Tiffany' }
                disabled={ studioStore.savingClasses || c.status === 'recording' }
                onChange={ (e) => { const voice = e.target.value; this.requirePro(() => { this.stopPreview(); studioStore.saveClasses({ voice }) }) } }>
                { VOICES.map(([v, label]) => <option key={ v } value={ v }>{ label }</option>) }
              </select>
              <span className="studio__hint">Changing the voice re-records all your announcements. It takes a minute or two.</span>
            </div>
          )
        }
        {
          setUp && this.renderToday(c)
        }
        { c.status === 'failed' && !setUp && <div className="studio__error" role="alert">{ status }</div> }
        <button type="button" className={ `studio__btn ${setUp ? 'studio__btn--ghost' : 'studio__btn--primary'}` } onClick={ () => this.requirePro(() => { this.stopPreview(); studioStore.openTimetable() }) }>
          { (c.classes || []).length ? 'Edit your timetable' : 'Set up from your timetable' }
        </button>
      </div>
    )
  }

  // Today's classes with a play button each, so the announcements can be heard
  // without opening the timetable
  renderToday (c) {
    const { playingKey } = this.state
    const day = today()
    const list = (c.classes || []).filter((k) => k.day === day).slice().sort((a, b) => String(a.time).localeCompare(String(b.time)))
    const shown = list.slice(0, 8)
    return (
      <div className="studio__group">
        <span className="studio__label">{ list.length ? `Today (${DAY_NAMES[day]})` : `No classes today (${DAY_NAMES[day]})` }</span>
        {
          shown.length > 0 && (
            <ul className="studio__classes-today" aria-label="Today's class announcements">
              {
                shown.map((k) => {
                  const key = `today-${k.id || `${k.time}-${k.name}`}`
                  return (
                    <li key={ key } className="studio__item">
                      <button type="button" className="studio__play"
                        aria-label={ playingKey === key ? 'Stop' : `Listen to the ${k.name} announcement` }
                        disabled={ !k.clipUrl }
                        onClick={ () => this.preview(key, k.clipUrl) }>
                        { playingKey === key ? <PauseIcon /> : <PlayIcon /> }
                      </button>
                      <div className="studio__item-text">
                        <span className="studio__item-title">{ k.time } · { k.name }</span>
                        <span className="studio__item-meta">{ k.room ? `${k.room} · ` : '' }{ k.clipUrl ? 'Tap to listen' : (c.status === 'recording' ? 'Recording…' : 'Not recorded yet') }</span>
                      </div>
                    </li>
                  )
                })
              }
            </ul>
          )
        }
        { list.length > shown.length && <span className="studio__hint">And { list.length - shown.length } more. All classes are under "Edit your timetable".</span> }
      </div>
    )
  }

  updateRow (i, field, value) {
    const { studioStore } = this.props
    const rows = studioStore.timetable.slice()
    rows[i] = { ...rows[i], [field]: value }
    studioStore.timetable = rows
  }

  removeRow (i) {
    const { studioStore } = this.props
    studioStore.timetable = studioStore.timetable.filter((r, n) => n !== i)
  }

  addRow (day) {
    const { studioStore } = this.props
    const rows = studioStore.timetable
    // Add after that day's last class, so it shows at the bottom of the day's folder
    let at = rows.length
    for (let n = rows.length - 1; n >= 0; n--) { if (rows[n].day === day) { at = n + 1; break } }
    studioStore.timetable = rows.slice(0, at).concat([{ id: newRowId(), day, time: '18:00', name: '', room: '' }], rows.slice(at))
    this.openDay(day, true)
  }

  openDay (day, open) {
    const openDays = this.state.openDays.filter((d) => d !== day)
    this.setState({ openDays: open ? openDays.concat([day]) : openDays })
  }

  renderRow (r, i, clips) {
    const { playingKey } = this.state
    return (
      <li key={ r.id || i } className="studio__class">
        <select className="studio__input studio__input--day" aria-label="Day" value={ r.day } onChange={ (e) => { this.updateRow(i, 'day', e.target.value); this.openDay(e.target.value, true) } }>
          { DAYS.map((d) => <option key={ d } value={ d }>{ d }</option>) }
        </select>
        <input className="studio__input studio__input--time" type="time" aria-label="Start time" value={ r.time }
          onChange={ (e) => this.updateRow(i, 'time', e.target.value) } />
        <input className="studio__input studio__input--name" type="text" aria-label="Class name" placeholder="Class" maxLength="60" value={ r.name }
          onChange={ (e) => this.updateRow(i, 'name', e.target.value) } />
        <input className="studio__input studio__input--room" type="text" aria-label="Room" placeholder="Room (optional)" maxLength="40" value={ r.room }
          onChange={ (e) => this.updateRow(i, 'room', e.target.value) } />
        {
          clips[r.id] && (
            <button type="button" className="studio__play" aria-label={ playingKey === `class-${r.id}` ? 'Stop' : `Listen to the ${r.name} announcement` }
              onClick={ () => this.preview(`class-${r.id}`, clips[r.id]) }>
              { playingKey === `class-${r.id}` ? <PauseIcon /> : <PlayIcon /> }
            </button>
          )
        }
        <button type="button" className="studio__remove" aria-label={ `Remove ${r.name || 'this class'}` } onClick={ () => this.removeRow(i) }>×</button>
      </li>
    )
  }

  // One folder per day, Monday to Sunday; today's is open to start with
  renderDays (rows, clips) {
    const { openDays } = this.state
    const now = today()
    return (
      <div className="studio__days studio__folders">
        {
          DAYS.map((day) => {
            const list = rows.map((r, i) => [r, i]).filter(([r]) => r.day === day)
            const open = openDays.indexOf(day) >= 0
            const times = list.map(([r]) => r.time).filter(Boolean).sort()
            return (
              <div key={ day } className="studio__folder">
                <button type="button" className="studio__folder-toggle" aria-expanded={ open } onClick={ () => this.openDay(day, !open) }>
                  <span>
                    { DAY_NAMES[day] }{ day === now ? ' (today)' : '' }
                    <span className="studio__folder-count">{ list.length ? plural(list.length, 'class') : 'none' }</span>
                    { times.length > 1 && <span className="studio__folder-span">{ times[0] }–{ times[times.length - 1] }</span> }
                  </span>
                  <span aria-hidden="true">{ open ? '−' : '+' }</span>
                </button>
                {
                  open && (
                    <div className="studio__day">
                      { list.length > 0 && <ul className="studio__timetable" aria-label={ `${DAY_NAMES[day]} classes` }>{ list.map(([r, i]) => this.renderRow(r, i, clips)) }</ul> }
                      <button type="button" className="studio__link" onClick={ () => this.addRow(day) }>+ Add a class on { DAY_NAMES[day] }</button>
                    </div>
                  )
                }
              </div>
            )
          })
        }
      </div>
    )
  }

  readTimetable (e) {
    e.preventDefault()
    let url = (this.props.studioStore.timetableUrl || '').trim()
    if (url && !/^https?:\/\//i.test(url)) url = `https://${url}`
    this.props.studioStore.readTimetable(url)
  }

  renderTimetable () {
    const { studioStore } = this.props
    const c = studioStore.classes || {}
    const lead = Number(c.lead) || 15
    const rows = studioStore.timetable || []
    const valid = rows.filter((r) => r.name.trim() && /^\d{2}:\d{2}$/.test(r.time))
    const clips = {}
    ;(c.classes || []).forEach((k) => { if (k.clipUrl) clips[k.id] = k.clipUrl })
    const voice = this.state.classVoice || c.voice || 'Tiffany'
    return (
      <div className="studio__screen">
        <header className="studio__header">
          <button type="button" className="studio__back" aria-label="Back" onClick={ () => { this.stopPreview(); studioStore.closeTimetable() } }><BackIcon /></button>
          <h2 className="studio__title">Your timetable</h2>
        </header>

        <form className="studio__group" onSubmit={ (e) => this.readTimetable(e) }>
          <label className="studio__label" htmlFor="studio-timetable">Your timetable page</label>
          <input id="studio-timetable" className="studio__input" type="text" inputMode="url" autoCapitalize="none" spellCheck="false"
            placeholder="e.g. mygym.ie/timetable" value={ studioStore.timetableUrl }
            onChange={ (e) => { studioStore.timetableUrl = e.target.value } } />
          <button type="submit" className="studio__btn studio__btn--ghost" disabled={ studioStore.reading || !studioStore.timetableUrl.trim() }>
            { studioStore.reading ? 'Reading your timetable… (up to 2 minutes)' : rows.length ? 'Read it again' : 'Read my timetable' }
          </button>
          <span className="studio__hint">We read the classes from the page. Check every day, time and name below; you can also add classes by hand.</span>
        </form>

        { studioStore.classesError && <div className="studio__error" role="alert">{ studioStore.classesError }</div> }

        {
          isDevice()
            ? this.renderDays(rows, clips)
            : (
              <div>
                { rows.length > 0 && <ul className="studio__timetable" aria-label="Classes">{ rows.map((r, i) => this.renderRow(r, i, clips)) }</ul> }
                <button type="button" className="studio__link" onClick={ () => this.addRow(rows.length ? rows[rows.length - 1].day : 'Mon') }>+ Add a class</button>
              </div>
            )
        }

        <div className="studio__row">
          <div className="studio__group">
            <label className="studio__label" htmlFor="studio-class-voice">Voice</label>
            <select id="studio-class-voice" className="studio__input" value={ voice } onChange={ (e) => this.setState({ classVoice: e.target.value }) }>
              { VOICES.map(([v, label]) => <option key={ v } value={ v }>{ label }</option>) }
            </select>
          </div>
          <div className="studio__group">
            <span className="studio__label">Members hear</span>
            <span className="studio__hint">"{ announcementText(valid[0] || { name: 'Spin', room: 'Studio 1' }, lead) }"</span>
          </div>
        </div>

        <p className="studio__hint">
          Each announcement plays at the first gap between songs { lead } minutes before the class, ahead of any ad. Change the timing on the Ads panel.
        </p>
        <button type="button" className="studio__btn studio__btn--primary studio__btn--big"
          disabled={ studioStore.savingClasses || valid.length === 0 }
          onClick={ () => { this.stopPreview(); studioStore.saveClasses({ url: studioStore.timetableUrl, classes: valid, voice, approve: true, enabled: true }) } }>
          { studioStore.savingClasses ? 'Saving…' : `Approve ${valid.length} ${valid.length === 1 ? 'class' : 'classes'} and record` }
        </button>
      </div>
    )
  }


  render () {
    const { studioStore } = this.props
    if (!studioStore.classes) return null
    const body = studioStore.classesScreen === 'timetable' ? this.renderTimetable() : this.renderSummary()
    return <section className="studio studio--classes" aria-label="Class announcements">{ body }</section>
  }
}

export default inject((stores) => ({
  studioStore: stores.studioStore,
  playlistStore: stores.playlistStore,
  userStore: stores.userStore
}))(observer(ClassesPanel))
